//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { CommonHelper } from '../../../../helpers/common-helper'
import { LocalizationHelper, ConvertHelper, MapHelper, TemplateHelper, ConfigHelper, TypeHelper } from '../../../../helpers'
import { EventsNames } from '../../../../helpers/events-names'
import { TrackEvents } from '../../../../helpers/track-events'

import { RouteTrackingService } from '../../../../services/route'
import { UserSessionService } from '../../../../services/user-session'

import { Enums } from '../../../../models/common/enums'
import { OpFile } from '../../../../models/core'
import { OpMarker, OpRange, OpCoord, OpTooltip, OpPolyline } from '../../../../models/custom/op-map-area'

import { GridOptions, GridTypes, GridController } from '../../../common/grid'
import { MapOptions, Map, MapEvent } from '../../../common/map2/map.directive.controller'
import { IMRBaseControlController } from '../../../common/base'

import { RouteTracking, RouteTrackingOptions } from './routes-tracking.interfaces'

interface DeviceOnMap {
    idLocation: number
    latitude: number
    longitude: number
    serialNbr: number
    marker?: OpMarker
    stepNumber: number
}

interface DeviceReading {
    text: string
    time: any
    stepNumber: number
}

interface TrackStep {
    latitude: number
    longitude: number
    stepNumber: number
    marker?: OpMarker
    deviceReadings: DeviceReading[]
}

interface DeviceMeasurement {
    serialNbr: number
    stepNumber: number
}

interface MapRange {
    latitude: number
    longitude: number
    range: number
    stepNumber: number
    circle: OpRange
}


export const CurrentPositionImagePath: string = './images/person_pin_big.png'

export class RouteTrackingDirectiveController
    extends IMRBaseControlController<RouteTracking, RouteTrackingOptions>
    implements RouteTracking {

    //#region Consts

    private get RECEIVED_CALL_MARKER_COLOR(): string { return 'orange' }
    private get RECEIVED_MEASUREMENTS_MARKER_COLOR(): string { return 'green' }

    //#endregion

    //#region Members

    public isLoading: boolean

    public isStartButtonDisabled: boolean
    public isPauseButtonDisabled: boolean
    public isPrevButtonDisabled: boolean
    public isNextButtonDisabled: boolean
    public isFirstButtonDisabled: boolean
    public isLastButtonDisabled: boolean

    public startButtonOptions: DevExpress.ui.dxButtonOptions
    public pauseButtonOptions: DevExpress.ui.dxButtonOptions
    public prevButtonOptions: DevExpress.ui.dxButtonOptions
    public nextButtonOptions: DevExpress.ui.dxButtonOptions
    public firstButtonOptions: DevExpress.ui.dxButtonOptions
    public lastButtonOptions: DevExpress.ui.dxButtonOptions
    public timeSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    public fileSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions

    public mapOptions: MapOptions
    public gridOptions: GridOptions
    public sliderOptions: DevExpress.ui.dxSliderOptions

    public currentTimeInfo: string
    public readDevicesInfo: string

    private devicesOnMap: DeviceOnMap[] = []
    private gridId: any
    private grid: GridController<any>
    private dataGrid: DevExpress.ui.dxDataGrid
    private slider: DevExpress.ui.dxSlider
    private lastReadTime: any
    private lastTimeoutStart: number = 0
    private map: Map
    private mapRanges: MapRange[] = []
    private receivedCallsFromDevices: DeviceMeasurement[] = []
    private receivedMeasurementsFromDevices: DeviceMeasurement[] = []
    private timeout: ng.IPromise<void>
    private tooltip: OpTooltip
    private trackOnMap: TrackStep[] = []
    private trackPolyline: OpPolyline

    private playInterval: number
    private currentStep: number

    private gridData: any[]

    //#endregion

    //#region Ctor

    constructor(
        private $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        private routeTrackingService: RouteTrackingService,
        private userSessionService: UserSessionService
    ) {
        super()

        this.isLoading = true
    }

    //#endregion

    //#region Lifecycle

    protected initialize(): JQueryPromise<void> {
        const trackFile: OpFile = _.first(this.options.tracks)

        return $.when(
            this.initializeGrid(),
            this.initializeMap(),
            this.initializeFileSelectBox(),
            this.initializeSlider(),
            this.initializeTimeSelectBox(),
            this.initializeControlButtons()
        ).then(() => {
            if (trackFile != null) {
                this.reloadData(this.getTextContentFromFile(trackFile))
                this.resetInfoPanel()
            } else {
                this.resetButtons()
                this.clearAll()
                this.reloadData(null)
            }

            this.isLoading = false
        })
    }

    protected destroy(): void {
        this.resetInterval()
    }

    //#endregion

    //#region Grid

    private initializeGrid(): JQueryPromise<any> {
        this.gridOptions = {
            columnAutoWidth: false,
            columnFixing: false,
            columnsDictionary: this.routeTrackingService.getTrackingGridColumns(),
            idReferenceType: Enums.ReferenceType.None,
            name: null,
            type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,
            skipDeselectingOnPageChange: true,

            buttonsOptions: {
                enabled: false
            },

            masterDetail: {
                enabled: true,
                template: "detail",
                autoExpandAll: true
            },

            selection: {
                mode: 'single'
            },

            onInitialized: (e: any, grid: GridController<any>) => {
                this.dataGrid = e.component
                this.grid = grid
                this.gridId = e.tableId
            },

            onSelectionChanged: (e) => {
                const selectedData = _.first<any>(e.selectedRowsData)

                if (selectedData != null) {
                    this.selectStep(selectedData.id)
                    this.grid.scrollToSelected()
                }
            },

            onRowClick: (e) => {
                this.pause()
            },
        }

        return super.waitForOnInitialized(this.gridOptions)
    }

    private selectGridRow(id: number): void {
        if (_.first(this.grid.dxGrid.getSelectedRowKeys()) !== id) {

            const pageSize: number = this.dataGrid.pageSize()
            const page: number = Math.floor(id / pageSize)

            this.dataGrid.pageIndex(page)
            this.dataGrid.selectRows([id], false)
        }
    }

    private updateGridData(): void {
        this.$scope.$broadcast(EventsNames.UPDATE_GRID_DATASOURCE + this.gridId, new DataSource({
            store: {
                type: 'array',
                key: 'id',
                data: this.gridData
            }
        }))
    }

    private resetGrid(): void {
        this.gridData = []

        if (this.dataGrid != null) {
            this.dataGrid.pageIndex(0)
            this.dataGrid.clearSelection()
            this.dataGrid.option('dataSource', this.gridData)
            this.dataGrid.refresh()
        }
    }
    //#endregion

    //#region Steps

    private isLastStep(): boolean {
        return this.currentStep === this.grid.dxGrid.totalCount() - 1;
    }

    private selectStep(stepNumber: number): void {
        if (stepNumber > -1 && stepNumber != this.currentStep) {
            this.currentStep = stepNumber

            this.selectGridRow(stepNumber)
            this.setSliderValue(stepNumber >= 0 ? stepNumber : 0)
            this.updateButtons()

            const gridData = _.find(this.gridData, (a) => a.id === stepNumber)

            this.stepChanged(gridData)
        }
    }

    private stepChanged(row: any): void {
        if (row == null) {
            return
        }

        this.hideToast()
        this.hideTooltip()

        this.drawRanges(row.id)
        this.drawTracks(row.id)
        this.resetMapMarkers(row.id)

        this.highlightDevices(row.id, this.receivedCallsFromDevices, this.RECEIVED_CALL_MARKER_COLOR)          // Highlight all devices which made call
        this.highlightDevices(row.id, this.receivedMeasurementsFromDevices, this.RECEIVED_MEASUREMENTS_MARKER_COLOR)    // Highlight all devices with measurements as far
        this.prepareSummaryTooltips(row.id)

        const readDevices: DeviceMeasurement[] = _.uniq(this.receivedMeasurementsFromDevices.filter(dev => dev.stepNumber <= this.currentStep), val => {
            return val.serialNbr
        })

        this.updateReadLocationsPanel(_.size(readDevices))
        this.updatePanelTime(this.gridData[this.currentStep].time)

        if ([TrackEvents.Processed, TrackEvents.FrameProcessed, TrackEvents.EmptyReadout, TrackEvents.ReadoutRecived, TrackEvents.ReciveWMBUS, TrackEvents.ReciveDiagnostic, TrackEvents.ReadDiagnostic].indexOf(row.eventtype) > -1) {
            const selectedDevice: DeviceOnMap = _.find(this.devicesOnMap, (device: DeviceOnMap) => {
                return device.serialNbr == row.serialNbr
            })

            if (selectedDevice != null) {
                this.openInfoTooltip(selectedDevice.marker, row.eventinfo)
                this.setZoom()
            }
            else {
                this.showToast(row.eventinfo, 'info')
            }
        }
        else if ([TrackEvents.LocationDescrStart, TrackEvents.LocationDescrEnd].indexOf(row.eventtype) > -1) {  // Clicked row with adding new device location
            const selectedDevice: DeviceOnMap = _.find(this.devicesOnMap, (device: DeviceOnMap) => {
                return device.serialNbr == row.serialNbr
            })

            this.setZoom()
            this.openInfoTooltip(selectedDevice.marker, row.eventinfo)
        }
        else {
            this.showToast(row.eventinfo, 'info')
        }
    }

    //#endregion

    //#region Track parsing

    private validateLine(line: string, index: number) {
        const timeRegex: RegExp = /\d{1,2}:\d{1,2}:\d{1,2}/g
        let time: RegExpExecArray = timeRegex.exec(line)

        const eventRegex: RegExp = /\[EventID: (\d+)\]/g
        let event: RegExpExecArray = eventRegex.exec(line)

        const lineRegex: RegExp = /\[Line: \d+\] /g
        lineRegex.exec(line)
        let eventInfo: string = line.substring(lineRegex.lastIndex || 0 + 1)

        if (!time && !event && this.devicesOnMap.length > 0) {
            time = this.lastReadTime
            eventInfo = line
            event = <any>[' ', TrackEvents.LocationDescrStart]
        }


        if (time && event && eventInfo && [0, TrackEvents.ExecuteMethod, TrackEvents.Read, TrackEvents.Write, TrackEvents.LocationDescrEnd].indexOf(parseInt(event[1])) < 0) {
            this.lastReadTime = time

            const gridRow: any = {
                id: index,
                time: time[0],
                eventtype: parseInt(event[1]),
                eventname: LocalizationHelper.getLocalizedText(this.getEventName(parseInt(event[1])), {
                    collection: 'Tracking'
                }),

                eventinfo: this.replaceNumbersInString(eventInfo)
            }

            this.checkEventId(parseInt(event[1]), eventInfo, gridRow, index, time[0])

            const keys: string[] = Object.keys(TrackEvents)

            if ([
                TrackEvents.LocationDescrStart,
                TrackEvents.ARANGERange
            ].indexOf(parseInt(event[1])) > -1 || !_.find(keys, key => {
                return TrackEvents[key] == parseInt(event[1])
            }))
                return index
            this.gridData.push(gridRow)

            return ++index
        }
        return index
    }

    private checkEventId(eventId: number, eventInfo: string, row: any, index: number, time: string) {
        if ([TrackEvents.Processed, TrackEvents.FrameProcessed, TrackEvents.LocationDescrEnd, TrackEvents.ReciveDiagnostic, TrackEvents.ReadDiagnostic].indexOf(eventId) > -1) { // frames
            const regex = /SERIAL_NBR=(\d+)/g
            const serialNbr = regex.exec(eventInfo)

            if (serialNbr) {
                const data = <DeviceMeasurement>{
                    serialNbr: parseInt(serialNbr[1]),
                    stepNumber: index
                };

                (<any>Object).assign(row, data)
            }
        }
        else if ([TrackEvents.LocationDescrStart].indexOf(eventId) > -1) { // new location point
            const latRegex: RegExp = /(LOCATION_)?LATITUDE=(\d+(\.|,)?\d*)/g
            const lngRegex: RegExp = /(LOCATION_)?LONGITUDE=(\d+(\.|,)?\d*)/g
            const serialNbrRegex: RegExp = /SERIAL_NBR=(\d+)/g
            const idLocationRegex: RegExp = /ID_LOCATION=(\d+)/g

            let lat: RegExpExecArray = latRegex.exec(eventInfo)
            let lng: RegExpExecArray = lngRegex.exec(eventInfo)
            let serialNbr: RegExpExecArray = serialNbrRegex.exec(eventInfo)
            let idLocation: RegExpExecArray = idLocationRegex.exec(eventInfo)

            let latT: string
            if (lat) {
                latT = lat[2] != null ? lat[2].replace(',', '.') : null
            } else {
                latT = null
            }

            let lngT: string
            if (lng) {
                lngT = lng[2] != null ? lng[2].replace(',', '.') : null
            } else {
                lngT = null
            }

            let latitude: number = parseFloat(latT)
            let longitude: number = parseFloat(lngT)

            if (latitude < -90 || latitude > 90) {
                latitude = 0
            }

            if (longitude < -180 || longitude > 180) {
                longitude = 0
            }

            let serial: string
            if (!serialNbr) {
                serial = null
            } else {
                serial = serialNbr[1]
            }

            let idLoc: string
            if (!idLocation) {
                idLoc = null
            } else {
                idLoc = idLocation[1]
            }
            const deviceData: DeviceOnMap = <DeviceOnMap>{
                idLocation: parseInt(idLoc),
                latitude: latitude,
                longitude: longitude,
                serialNbr: parseFloat(serial),
                stepNumber: index
            }

            const existingDeviceOnMap: DeviceOnMap = _.find(this.devicesOnMap, (deviceOnMap: DeviceOnMap) => {
                return deviceOnMap.serialNbr === deviceData.serialNbr
            })

            if (existingDeviceOnMap == null) {
                this.devicesOnMap.push(deviceData);
            }

            (<any>Object).assign(row, deviceData)
        }
        else if (eventId === TrackEvents.CurGPS) { // GPS new location or information about error
            const latRegex = /LATITUDE: (\d+(\.|,)?\d*)/g
            const lngRegex = /LONGITUDE: (\d+(\.|,)?\d*)/g

            const lat = latRegex.exec(eventInfo)
            const lng = lngRegex.exec(eventInfo)

            if (lat && lng) {
                lat[1] = lat[1].replace(',', '.')
                lng[1] = lng[1].replace(',', '.')

                let marker = new OpMarker()
                let coord = new OpCoord()
                coord.latitude = parseFloat(lat[1])
                coord.longitude = parseFloat(lng[1])
                marker.coords = coord
                marker.iconURL = 'https://maps.gstatic.com/intl/en_us/mapfiles/markers2/measle_red.png'

                const trackStep: TrackStep = <TrackStep>{
                    latitude: parseFloat(lat[1]),
                    longitude: parseFloat(lng[1]),
                    stepNumber: index,
                    marker: this.map.addMarker(marker),
                    deviceReadings: []
                };

                this.trackOnMap.push(trackStep);
                (<any>Object).assign(row, trackStep)
            }
        }
        else if ([119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 129, 130, 131, 132, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152].indexOf(eventId) > -1) { // Device informations
            if (this.trackOnMap && this.trackOnMap.length > 0) {
                const lastStep: TrackStep = this.trackOnMap[this.trackOnMap.length - 1]

                lastStep.deviceReadings.push({
                    stepNumber: index,
                    text: this.replaceNumbersInString(eventInfo),
                    time: time
                })
            }
        }
        else if (eventId === TrackEvents.ARANGERange) { // Range
            const arangeRegex: RegExp = /ARANGE_RANGE=(\d+)/g
            const arange: RegExpExecArray = arangeRegex.exec(eventInfo)

            if (this.trackOnMap && this.trackOnMap.length > 0 && arange) {
                const lastPosition: TrackStep = this.trackOnMap[this.trackOnMap.length - 1]

                //strokeColor: string, strokeOpacity: number, strokeWeight: number, fillColor: string, fillOpacity: number
                const range: OpRange = new OpRange()
                const lastPositionCoords: OpCoord = new OpCoord()

                lastPositionCoords.latitude = lastPosition.latitude
                lastPositionCoords.longitude = lastPosition.longitude

                range.coords = lastPositionCoords
                range.radius = parseInt(arange[1])
                range.lineColor = '#0000FF'
                range.lineOpacity = 0.8
                range.lineWeight = 1
                range.fillColor = '#0000FF'
                range.fillOpacity = 0.05

                const circle = this.map.addRange(range)
                const mapRange = {
                    latitude: lastPosition.latitude,
                    longitude: lastPosition.longitude,
                    range: parseInt(arange[1]),
                    stepNumber: index,
                    circle: circle
                }

                this.mapRanges.push(mapRange);
                (<any>Object).assign(row, mapRange)
            }
        }
        else if ([TrackEvents.ReadoutRecived, TrackEvents.EmptyReadout].indexOf(eventId) > -1) { // Device measurements
            const serialNbrRegex: RegExp = /SERIAL_NBR=(\d+)/g
            const serialNbr: RegExpExecArray = serialNbrRegex.exec(eventInfo)

            if (serialNbr != null) {
                const data = <DeviceMeasurement>{
                    serialNbr: parseInt(serialNbr[1]),
                    stepNumber: index
                }

                this.receivedMeasurementsFromDevices.push(data);
                (<any>Object).assign(row, data)
            }
        }
        else if (eventId === TrackEvents.ReciveWMBUS) { // Device ready calls
            const serialNbrRegex: RegExp = /SERIAL_NBR=(\d+)/g
            const serialNbr: RegExpExecArray = serialNbrRegex.exec(eventInfo)

            if (serialNbr != null) {
                const data = <DeviceMeasurement>{
                    serialNbr: parseInt(serialNbr[1]),
                    stepNumber: index
                }

                this.receivedCallsFromDevices.push(data);
                (<any>Object).assign(row, data)
            }
        }
    }

    private removeNumbersFromString(text: string): string {
        const regex: RegExp = /((\d+(\.|,)?\d*)[,:\/]*)+/g
        let changedText: string = text

        // finding all numbers
        const result: RegExpMatchArray = text.match(regex)

        // if no numbers in string return default text
        if (result != null) {
            result.forEach((number: string) => {
                changedText = changedText.replace(number, '')
            })
        }

        return changedText
    }

    private replaceNumbersInString(text: string): string {
        const regex = /((\d+(\.|,)?\d*)[,:\/]*)+/g
        const locationNameRegex = /LOCATION_NAME=(([\w\/\.,ĄĆĘŁŃÓŚŹŻąćęłńóśźż]*\s*)*);/gi
        const frameRegex = /FRAME=(\w*)/g
        const deviceRegex = /(urz(a|ą)dze(n|a|ń))(\W|_)/gi
        let changedText = text

        // finding location name
        const locationNameResult: RegExpExecArray = locationNameRegex.exec(text)

        if (locationNameResult != null) {
            changedText = changedText.replace(locationNameResult[1], '{location_name}')
        }

        changedText = changedText.split(' ').join('_')

        // finding frame
        const frameResult: RegExpExecArray = frameRegex.exec(text)

        if (frameResult != null) {
            changedText = changedText.replace(frameResult[1], '{frame}')
        }

        // finding device
        const deviceResult: RegExpExecArray = deviceRegex.exec(text)

        if (deviceResult != null) {
            changedText = changedText.replace(deviceResult[1], '{devices}')
        }

        let index = 0

        // finding all numbers
        const result: RegExpMatchArray = changedText.match(regex)

        // if no numbers in string return default text
        if (result != null) {
            let charIndex = 0 // index in string of last replaced number

            result.forEach((number: string) => {
                changedText = changedText.substring(0, charIndex) + changedText.substring(charIndex).replace(number, '{0_' + index + '}')
                charIndex = changedText.indexOf('{0_' + index + '}') + 3 + index.toString().length
                index++
            })
        }

        // changing all null values
        changedText = changedText.split('<null>').join(' - ')

        // downloaded text from api
        let textFromApi: string = LocalizationHelper.getLocalizedText(changedText, {
            collection: 'Tracking'
        })

        textFromApi = CommonHelper.capitalizeFirstLetter(textFromApi)

        if (deviceResult != null) {
            let indexOfDevices: number = textFromApi.indexOf('{devices}')

            if (indexOfDevices == -1) {
                indexOfDevices = textFromApi.indexOf('{devices2}')
            }

            if (result != null) {
                for (let i = result.length - 1; i >= 0; i--) {
                    let indexOfNumber = textFromApi.indexOf('{0_' + i + '}')

                    if (indexOfNumber > -1 && indexOfNumber < indexOfDevices) {
                        let number = result[i]

                        if (number == '1') {
                            textFromApi = textFromApi.replace('{devices}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device, {
                                collection: 'Tracking'
                            }))

                            textFromApi = textFromApi.replace('{devices2}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device1, {
                                collection: 'Tracking'
                            }))
                        }
                        else if ((number.length == 1 && ['2', '3', '4'].indexOf(number.charAt(0)) > -1) ||
                            (number.length > 1 && ['2', '3', '4'].indexOf(number.charAt(number.length - 1)) > -1 && number.charAt(number.length - 2) != '1')) {
                            textFromApi = textFromApi.replace('{devices}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices, {
                                collection: 'Tracking'
                            }))

                            textFromApi = textFromApi.replace('{devices2}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device, {
                                collection: 'Tracking'
                            }))
                        }
                        else {
                            textFromApi = textFromApi.replace('{devices}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices, {
                                collection: 'Tracking'
                            }))

                            textFromApi = textFromApi.replace('{devices2}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices, {
                                collection: 'Tracking'
                            }))
                        }
                        break
                    }
                }
            }
            else {
                textFromApi = textFromApi.replace('{devices}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices, {
                    collection: 'Tracking'
                }))

                textFromApi = textFromApi.replace('{devices2}', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices, {
                    collection: 'Tracking'
                }))
            }
        }

        if (result) {
            index = 0
            // replacing numbers
            result.forEach(number => {
                textFromApi = textFromApi.replace('{0_' + index++ + '}', number)
            })
        }

        if (frameResult) {
            textFromApi = textFromApi.replace('{frame}', frameResult[1])
        }

        if (locationNameResult) {
            textFromApi = textFromApi.replace('{location_name}', locationNameResult[1])
        }

        textFromApi = textFromApi.split('_').join(' ')

        return textFromApi
    }

    private getEventName(eventId: number): string {
        for (const eventName in TrackEvents) {
            if (TrackEvents[eventName] == eventId) {
                return this.removeNumbersFromString(eventName)
            }
        }

        return eventId.toString()
    }

    //#endregion

    //#region Controls

    private run(): void {
        let time: number = this.playInterval < 1 ? 1 / this.playInterval * 1000 : 1000
        let now: number = this.userSessionService.getDataProvider().getDateTimeNow()

        time = time - (now - this.lastTimeoutStart)

        if (time < 0) {
            time = 0
        }

        this.startInterval(time, () => {
            this.lastTimeoutStart = this.userSessionService.getDataProvider().getDateTimeNow()
            this.nextStep(this.playInterval >= 1 ? this.playInterval : 1)

            if (!this.isLastStep()) {
                this.run()
            }
        })
    }

    private start(): void {
        this.isStartButtonDisabled = true

        if (this.currentStep == null || this.currentStep === this.dataGrid.totalCount() - 1) {
            this.selectStep(0)
        } else {
            this.updateButtons()
        }

        this.lastTimeoutStart = new Date().getTime()
        this.run()
    }

    private pause(): void {
        this.isStartButtonDisabled = false

        this.updateButtons()
        this.resetInterval()
    }

    private prevStep(): void {
        if (this.currentStep != null && this.currentStep > 0) {
            this.selectStep(this.currentStep - 1)
        }
    }

    private nextStep(stepSize: number = 1): void {
        if (this.currentStep != null) {
            const step: number = this.dataGrid.totalCount() - 1 - stepSize >= this.currentStep ? stepSize : this.dataGrid.totalCount() - this.currentStep - 1

            this.selectStep(this.currentStep + step)
        }
        else {
            this.dataGrid.selectRowsByIndexes([0])
        }
    }

    private first(): void {
        for (let i = this.trackOnMap.length - 1; i >= 0; i--) {
            const coordinates: TrackStep = this.trackOnMap[i]

            if (coordinates.stepNumber < this.currentStep) {
                this.selectStep(coordinates.stepNumber)

                return
            }
        }

        this.selectStep(0)
    }

    private last(): void {
        for (let i = 0; i < this.trackOnMap.length; i++) {
            const coordinates: TrackStep = this.trackOnMap[i]

            if (coordinates.stepNumber > this.currentStep) {
                this.selectStep(coordinates.stepNumber)

                return
            }
        }

        this.selectStep(this.dataGrid.totalCount() - 1)
    }

    //#endregion

    private highlightDevices(stepNumber: number, devicesList: DeviceMeasurement[], markerColor?: string): void {
        _.forEach(devicesList, (deviceMeasurement: DeviceMeasurement) => {
            if (deviceMeasurement.stepNumber <= stepNumber) {
                const currentDevice: DeviceOnMap = _.find(this.devicesOnMap, (deviceOnMap: DeviceOnMap) => {
                    return deviceOnMap.serialNbr == deviceMeasurement.serialNbr
                })

                if (currentDevice != null) {
                    this.highlightMarker(currentDevice.marker, markerColor)
                }
            }
        })
    }

    private reloadData(text: string): void {
        if (text != null) {
            this.loadFile(text)
            this.updateGridData()
            this.updateMarkers()
        }
    }

    private clearAll(): void {
        this.currentStep = 0

        this.resetInterval()
        this.resetInfoPanel()
        this.resetSlider()
        this.resetGrid()
        this.hideTooltip()

        this.devicesOnMap.forEach((device: DeviceOnMap) => {
            if (device.marker && device.marker.isSetOnMap()) {
                device.marker.hide()
            }
        })
        this.devicesOnMap = []
        this.trackOnMap.forEach((step: TrackStep) => {
            if (step.marker && step.marker.isSetOnMap()) {
                step.marker.hide()
            }
        })
        this.trackOnMap = []

        this.mapRanges.forEach((range: MapRange) => {
            if (range.circle && range.circle.isSetOnMap()) {
                range.circle.hide()
            }
        })
        this.mapRanges = []
        this.receivedCallsFromDevices = []
        this.receivedMeasurementsFromDevices = []

        if (this.trackPolyline && this.trackPolyline.isSetOnMap()) {
            this.trackPolyline.hide()
        }
        this.trackPolyline = null
    }

    //#region Files

    private initializeFileSelectBox(): JQueryPromise<any> {
        const currentFile: OpFile = _.first(this.options.tracks)
        const dataSource = _.map(this.options.tracks, (track: OpFile) => {
            return {
                id: track.idFile,
                caption: track.physicalFileName
            }
        })

        this.fileSelectBoxOptions = {
            displayExpr: 'caption',
            valueExpr: 'id',
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            value: currentFile != null ? currentFile.idFile : null,
            dataSource,

            onValueChanged: e => {
                if (!e.value) {
                    return
                }

                this.resetButtons()
                this.clearAll()

                const currentFile: OpFile = _.find(this.options.tracks, (track: OpFile) => {
                    return track.idFile === e.value
                })

                if (currentFile != null) {
                    this.reloadData(this.getTextContentFromFile(currentFile))
                }
            }
        }

        return super.waitForOnInitialized(this.fileSelectBoxOptions)
    }

    private getTextContentFromFileBytes(fileBytes: string | number[]): string {
        if (_.isArray(fileBytes)) {
            return ConvertHelper.byteArrayToUnicodeString(fileBytes as number[])
        }

        return fileBytes as string
    }

    private getTextContentFromFile(trackFile: OpFile): string {
        return this.getTextContentFromFileBytes(trackFile.fileBytes)
    }

    private loadFile(text: string): void {
        const lines: string[] = text.split(/\r\n|\n/)
        this.gridData = []

        let index: number = 0
        lines.forEach((line: string) => {
            index = this.validateLine(line, index)
        })

        this.setSliderMaxValue(index - 1)
    }

    //#endregion

    //#region Map

    private initializeMap(): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()

        MapHelper
            .getDefaultOptions(this.userSessionService.getDataProvider())
            .then((mapOptions: MapOptions) => {
                mapOptions.hideCloseButton = true
                mapOptions.hideToggleClusteringButton = true
                mapOptions.hideLegend = true
                mapOptions.hideCounter = true
                mapOptions.disableMapTypeControl = true
                mapOptions.disableStreetViewControl = true

                mapOptions.onMapInit = (map: Map) => {
                    this.map = map

                    deferred.resolve()
                }

                this.mapOptions = mapOptions
            })

        return deferred.promise()
    }

    private setZoom(): void {
        if (this.map.getZoom() < 16) {
            this.map.setZoom(16)
        }
    }

    private zoomCenter(): void {
        if (this.devicesOnMap.length == 1) {
            this.map.setCenter(this.devicesOnMap[0].marker.getPosition())
            this.setZoom()
        } else {
            this.map.fitBounds(this.devicesOnMap.map((d: DeviceOnMap) => {
                const coord: OpCoord = new OpCoord()

                coord.latitude = d.latitude
                coord.longitude = d.longitude

                return coord
            }));
        }
    }

    private drawRanges(stepNumber: number): void {
        let lastStepIndex = -1

        for (let i = this.mapRanges.length - 1; i >= 0; i--) {
            let range = this.mapRanges[i]

            if (range.stepNumber <= stepNumber) {
                if (!range.circle.isShown()) {
                    range.circle.show()
                }

                lastStepIndex = i
                break
            }
            else {
                if (range.circle.isShown()) {
                    range.circle.hide()
                }
            }
        }

        for (let i = lastStepIndex - 1; i >= 0; i--) {
            this.mapRanges[i].circle.hide()
        }
    }

    private prepareSummaryTooltips(stepNumber: number): void {
        if (this.trackOnMap && this.trackOnMap.length > 0) {
            _.forEach(this.trackOnMap, (step) => {
                let text = '<p>'
                let count = 0

                // calculating text for tooltip
                _.forEach(step.deviceReadings, (reading) => {
                    if (reading.stepNumber <= stepNumber) {
                        text += reading.time + ': ' + reading.text + '<br/>'
                        count++
                    }
                })

                if (count == 0) {
                    text += LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoInformation, {
                        collection: 'Tracking'
                    })
                }

                // remove previous listeners
                this.map.clearListeners(MapEvent.Click, step.marker)

                // add new click listener
                step.marker.addListener(Enums.SimaxMarkerEvent.Click, (event) => {
                    this.openInfoTooltip(step.marker, text)
                })
            })
        }
    }

    //#endregion

    //#region Markers

    private highlightMarker(marker: OpMarker, markerColor?: string) {
        if (marker && marker.isSetOnMap()) {
            // set different color of marker (green or orange)
            marker.setIcon('http://maps.google.com/mapfiles/ms/icons/' + (markerColor || 'green') + '-dot.png')
            // highlight marker
            marker.setZIndex(100)
        }
    }

    private resetMapMarkers(stepNumber: number): void {
        this.devicesOnMap.forEach((device) => {
            // setting default red color for marker
            if (device.marker && device.marker.isSetOnMap() && device.marker.isShown()) {
                device.marker.setIcon('http://maps.google.com/mapfiles/ms/icons/red-dot.png')

                // remove previous listeners
                this.map.clearListeners(MapEvent.Click, device.marker)
                device.marker.addListener(Enums.SimaxMarkerEvent.Click, (event) => {
                    for (let i = this.gridData.length - 1; i >= 0; i--) {
                        let row = this.gridData[i]

                        if (row.id <= stepNumber && row.serialNbr && row.serialNbr == device.serialNbr) {
                            this.openInfoTooltip(device.marker, row.eventinfo)
                            break
                        }
                    }
                })
            }
        })
    }

    private updateMarkers(): void {
        this.devicesOnMap.forEach((deviceOnMap: DeviceOnMap) => {
            const marker = new OpMarker()
            const coord = new OpCoord()

            coord.latitude = deviceOnMap.latitude
            coord.longitude = deviceOnMap.longitude
            marker.coords = coord
            marker.iconURL = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
            deviceOnMap.marker = this.map.addMarker(marker)
        })

        this.$timeout(() => {
            this.zoomCenter()
        })
    }

    //#endregion

    //#region Slider

    private initializeSlider(maxIndex: number = null): JQueryPromise<any> {
        this.sliderOptions = {
            min: 0,
            max: maxIndex != null ? maxIndex - 1 : 1,
            value: 0,
            label: {
                format: (value: number) => {
                    if (value == 0) {
                        return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Start)
                    }
                    return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.End)
                },
                position: "top",
                visible: true
            },

            tooltip: {
                enabled: true,
                format: (value) => {
                    if (_.isNumber(value) && this.dataGrid != null && this.gridData != null && this.gridData[value] != null) {
                        return this.gridData[value].time
                    }

                    return ''
                }
            },

            onInitialized: e => {
                this.slider = e.component
            },

            onValueChanged: _.debounce(e => {
                this.selectStep(e.value)
            }, 200),
        }

        return super.waitForOnInitialized(this.sliderOptions)
    }

    private resetSlider(): void {
        this.slider.reset()
    }

    private setSliderMaxValue(maxValue: number): void {
        this.slider.option('max', maxValue)
    }

    private setSliderValue(value: number): void {
        if (this.slider.option('value') !== value) {
            this.slider.option('value', value)
        }
    }

    //#endregion

    //#region Time SelectBox

    private initializeTimeSelectBox(): JQueryPromise<any> {
        this.timeSelectBoxOptions = {
            dataSource: [
                {
                    value: 0.1,
                    text: '>>0.1'
                },
                {
                    value: 0.2,
                    text: '>>0.2'
                },
                {
                    value: 0.5,
                    text: '>>0.5'
                },
                {
                    value: 1,
                    text: '>>1'
                },
                {
                    value: 2,
                    text: '>>2'
                },
                {
                    value: 5,
                    text: '>>5'
                },
                {
                    value: 10,
                    text: '>>10'
                },
                {
                    value: 20,
                    text: '>>20'
                },
                {
                    value: 50,
                    text: '>>50'
                },
                {
                    value: 100,
                    text: '>>100'
                },
                {
                    value: 500,
                    text: '>>500'
                }
            ],
            value: 1,
            valueExpr: 'value',
            displayExpr: 'text',
            width: 100,
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AccelerationChose, {
                collection: 'Tracking'
            }),

            bindingOptions: {
                disabled: '$ctrl.isStartButtonDisabled'
            },

            onValueChanged: e => {
                this.playInterval = e.value
            }
        }

        return super.waitForOnInitialized(this.timeSelectBoxOptions)
    }

    //#endregion

    //#region Timeout

    private startInterval(time: number, func: () => void): void {
        this.resetInterval()

        this.timeout = this.$timeout(() => {
            func()
        }, time)
    }

    private resetInterval(): void {
        if (this.timeout != null) {
            this.$timeout.cancel(this.timeout)
            this.timeout = null
        }
    }

    //#endregion

    //#region Tracks

    private drawTracks(stepNumber: number): void {
        if (this.trackPolyline) {
            this.trackPolyline.hide()
        }

        let latLngArray: OpCoord[] = []
        let lastStepIndex = -1

        for (let i = 0; i < this.trackOnMap.length; i++) {
            const step: TrackStep = this.trackOnMap[i]

            if (step.stepNumber <= stepNumber) {
                const coord = new OpCoord()

                coord.latitude = step.latitude
                coord.longitude = step.longitude

                latLngArray.push(coord)

                // set blue dot as an icon for marker
                step.marker.setIcon('https://maps.gstatic.com/intl/en_us/mapfiles/markers2/measle_blue.png', { width: 7, height: 7 })

                if (!step.marker.isShown()) {
                    step.marker.show()
                }
            }

            lastStepIndex = i

            if (step.stepNumber >= stepNumber) {
                if (step.stepNumber > stepNumber) {
                    lastStepIndex = i - 1
                }

                break
            }
        }

        for (let i = lastStepIndex + 1; i < this.trackOnMap.length; i++) {
            this.trackOnMap[i].marker.hide()
        }

        if (latLngArray.length > 0) {
            this.trackPolyline = new OpPolyline()
            this.trackPolyline.positions = latLngArray
            this.trackPolyline.lineColor = '#0000FF'
            this.trackPolyline.lineOpacity = 1.0
            this.trackPolyline.lineWeight = 2
            this.trackPolyline = this.map.addPolyline(this.trackPolyline)

            const lastStep: TrackStep = this.trackOnMap[lastStepIndex]

            if (lastStep != null) {
                lastStep.marker.setIcon(CurrentPositionImagePath)
                lastStep.marker.setZIndex(200)

                this.setZoom()
                this.map.setCenter(lastStep.marker.getPosition())
            }

            if (this.trackPolyline) {
                this.trackPolyline.show()
            }
        }
        else {
            if (this.trackPolyline) {
                this.trackPolyline.hide()
            }
        }
    }

    //#endregion

    //#region Toast

    private hideToast(): void {
        CommonHelper.closeInfoPopup()
    }

    private showToast(text: string, type: string): void {
        CommonHelper.openInfoPopup(text, type, this.playInterval, undefined, {
            position: {
                at: 'right bottom',
                my: 'right bottom',
                offset: '-10 -10'
            }
        })
    }

    //#endregion

    //#region Tooltip

    private hideTooltip(): void {
        if (this.tooltip) {
            this.tooltip.hide()
        }
    }

    private openInfoTooltip(marker: OpMarker, text: string): void {
        this.tooltip = this.map.createMarkerTooltip(marker, text.split(';').join('<br/>'))
        this.tooltip.show()
    }

    //#endregion

    //#region Control Buttons

    private initializeControlButtons(): JQueryPromise<void> {
        this.startButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Play),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.PLAY_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isStartButtonDisabled'
            },

            onClick: () => {
                this.start()
            },
        }

        this.pauseButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Pause),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.PAUSE_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isPauseButtonDisabled'
            },

            onClick: () => {
                this.pause()
            },
        }

        this.prevButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PreviousStep),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.PREV_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isPrevButtonDisabled'
            },

            onClick: () => {
                this.prevStep()
            },
        }

        this.nextButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NextStep),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.NEXT_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isNextButtonDisabled'
            },

            onClick: () => {
                this.nextStep()
            },
        }

        this.firstButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PreviousCoordinates),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.FIRST_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isFirstButtonDisabled'
            },

            onClick: () => {
                this.first()
            },
        }

        this.lastButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NextCoordinates),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.LAST_BUTTON, this.userSessionService),

            bindingOptions: {
                disabled: '$ctrl.isLastButtonDisabled'
            },

            onClick: () => {
                this.last()
            },
        }

        return TypeHelper.onInitializedAll(
            this.startButtonOptions,
            this.pauseButtonOptions,
            this.prevButtonOptions,
            this.nextButtonOptions,
            this.firstButtonOptions,
            this.lastButtonOptions
        )
    }

    private updateButtons(): void {
        const totalCount: number = this.dataGrid.totalCount()
        const pageIndex: number = this.dataGrid.pageIndex()

        if (this.currentStep >= 0) {
            // prev
            if (this.isStartButtonDisabled || this.currentStep == 0 && pageIndex == 0) {
                this.isPauseButtonDisabled = false
                this.isPrevButtonDisabled = true
                this.isFirstButtonDisabled = true
            }
            else if (!this.isStartButtonDisabled) {
                this.isPrevButtonDisabled = false
                this.isFirstButtonDisabled = false
            }

            // next
            if (this.isStartButtonDisabled) {
                this.isPauseButtonDisabled = false
                this.isNextButtonDisabled = true
                this.isLastButtonDisabled = true

                // stop
                if (this.currentStep >= totalCount - 1) {
                    this.isPauseButtonDisabled = true
                    this.isStartButtonDisabled = false
                    this.resetInterval()
                }
            }
            else if (this.currentStep >= totalCount - 1) {
                this.isNextButtonDisabled = true
                this.isLastButtonDisabled = true
            }
            else if (!this.isStartButtonDisabled) {
                this.isNextButtonDisabled = false
                this.isLastButtonDisabled = false
            }
        } else {
            this.isNextButtonDisabled = true
            this.isPrevButtonDisabled = true
        }
    }

    private resetButtons(): void {
        this.isFirstButtonDisabled = false
        this.isStartButtonDisabled = false
        this.isPauseButtonDisabled = true
        this.isLastButtonDisabled = false
        this.isPrevButtonDisabled = false
        this.isNextButtonDisabled = false
    }

    //#endregion

    //#region Info Panel

    private resetInfoPanel(): void {
        this.resetPanelTime()
        this.resetReadLocationsPanel()
    }

    private resetPanelTime(): void {
        this.updatePanelTime('00:00:00')
    }

    private updatePanelTime(time: string): void {
        this.currentTimeInfo = `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Time)}:  ${time}`
    }

    private resetReadLocationsPanel(): void {
        this.updateReadLocationsPanel(0)
    }

    private updateReadLocationsPanel(readDevicesCount: number): void {
        this.readDevicesInfo = `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReadLocations, { collection: 'Tracking' })}: ${readDevicesCount}/${_.size(this.devicesOnMap)}`
    }

    //#endregion
}